import { useEffect, useState } from 'react';
import {
  Box, Button, FormControl, FormLabel, Input, Select, VStack, HStack,
  Text, Badge, Table, Thead, Tbody, Tr, Th, Td, TableContainer,
  Collapse, Flex, Divider,
} from '@chakra-ui/react';
import { createAmbulanceRateCard, getRateCardByAmbulanceProviderId, updateRateCard } from '../AmbulanceProviders/Action/action';
import { ShowAlert } from '../../GlobalComponent/Alerts/Alert';

export interface AmbulanceRateCardProps {
  ambulanceProviderId: string;
  ambulanceProviderName: string;
  ambulanceType: string;
  vehicleType: string;
  rateCards: { distanceRange: string; rateType: string; prices: any }[];
}

const AMBULANCE_TYPES = ['ALS', 'BLS', 'ALS (with doctor/EMT)', 'ALS (with paramedic)'];

const emptyRow = () => ({ distanceRange: '', rateType: 'flat', prices: null as any });

function AmbulanceRateCard({
  clearFormTwo, setClearFormTwo, providers,
}: {
  clearFormTwo: Boolean;
  setClearFormTwo: any;
  providers: any;
}) {
  // Provider selection
  const [selectedProviderId, setSelectedProviderId] = useState('');
  const [selectedProviderName, setSelectedProviderName] = useState('');

  // All saved rate cards for the selected provider
  const [providerRateCards, setProviderRateCards] = useState<any[]>([]);
  const [expandedType, setExpandedType] = useState<string | null>(null);

  // Current type being added/edited
  const [ambulanceType, setAmbulanceType] = useState('');
  const [vehicleType, setVehicleType] = useState('');
  const [rateCards, setRateCards] = useState([emptyRow()]);
  const [editingRateCardId, setEditingRateCardId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (clearFormTwo) { handleFullClear(); setClearFormTwo(!clearFormTwo); }
    // eslint-disable-next-line
  }, [clearFormTwo]);

  // When provider changes, fetch its existing rate cards
  useEffect(() => {
    if (selectedProviderId) {
      fetchProviderRateCards(selectedProviderId);
    } else {
      setProviderRateCards([]);
    }
    resetTypeForm();
    // eslint-disable-next-line
  }, [selectedProviderId]);

  const fetchProviderRateCards = async (providerId: string) => {
    try {
      const result = await getRateCardByAmbulanceProviderId({ ambulanceProviderId: providerId });
      setProviderRateCards(result || []);
    } catch (e) {
      setProviderRateCards([]);
    }
  };

  // ── Row helpers ──

  const handleRateCardChange = (index: number, field: string, value: any) => {
    const updated = [...rateCards];
    updated[index] = { ...updated[index], [field]: field === 'prices' ? +value : value };
    setRateCards(updated);
  };

  const handleInclude = (index: number) => {
    const updated = [...rateCards];
    let val = updated[index].distanceRange.trim();
    if (!val) return;
    if (!val.endsWith(' km')) val = `${val} km`;
    updated[index].distanceRange = val;
    setRateCards(updated);
  };

  // ── Load an existing type into the form for editing ──
  const handleEditEntry = (rc: any) => {
    setAmbulanceType(rc.ambulanceType || '');
    setVehicleType(rc.vehicleType || '');
    setRateCards(rc.rateCards?.length ? rc.rateCards.map((r: any) => ({ ...r })) : [emptyRow()]);
    setEditingRateCardId(rc._id);
    setExpandedType(null);
  };

  // ── Save current type form ──
  const handleSave = async () => {
    if (!selectedProviderId) return ShowAlert('warning', 'Select a provider first');
    if (!ambulanceType || ambulanceType === 'Select Ambulance Type') return ShowAlert('warning', 'Select ambulance type');
    if (!vehicleType.trim()) return ShowAlert('warning', 'Enter vehicle type');
    const invalidRow = rateCards.findIndex((r) => !r.distanceRange.trim() || !r.rateType || r.prices == null);
    if (invalidRow !== -1) return ShowAlert('warning', 'Fill in distance range, rate type and price for all rows');

    setSaving(true);
    try {
      const payload: AmbulanceRateCardProps = {
        ambulanceProviderId: selectedProviderId,
        ambulanceProviderName: selectedProviderName,
        ambulanceType,
        vehicleType,
        rateCards,
      };

      if (editingRateCardId) {
        await updateRateCard(editingRateCardId, payload);
        ShowAlert('success', `${ambulanceType} updated`);
      } else {
        await createAmbulanceRateCard(payload);
        ShowAlert('success', `${ambulanceType} saved`);
      }

      resetTypeForm();
      await fetchProviderRateCards(selectedProviderId);
    } catch (e: any) {
      ShowAlert('warning', e?.response?.data?.message || 'Error saving rate card');
    } finally {
      setSaving(false);
    }
  };

  const resetTypeForm = () => {
    setAmbulanceType('');
    setVehicleType('');
    setRateCards([emptyRow()]);
    setEditingRateCardId(null);
  };

  const handleFullClear = () => {
    setSelectedProviderId('');
    setSelectedProviderName('');
    setProviderRateCards([]);
    setExpandedType(null);
    resetTypeForm();
  };

  const handleProviderChange = (e: any) => {
    const selected: any = providers.find((p: any) => p._id === e.target.value);
    setSelectedProviderId(selected?._id || '');
    setSelectedProviderName(selected?.operatorName || '');
  };

  // Already-saved types for this provider — used to skip in the dropdown
  const savedTypes = providerRateCards.map((rc: any) => rc.ambulanceType);
  const availableTypes = AMBULANCE_TYPES.filter(
    (t) => !savedTypes.includes(t) || t === ambulanceType
  );

  return (
    <Box display="flex" justifyContent="space-between" w="100%" gap={6}>

      {/* ── Left: Form ── */}
      <Box p={5} minW="50%" bg="gray.50" borderRadius="md" boxShadow="md">
        <Box mb={4}>
          <Text fontSize="lg" fontWeight="bold">Ambulance Provider Rate Card</Text>
        </Box>
        <VStack spacing={4} align="stretch">

          {/* Provider selector */}
          <FormControl>
            <FormLabel>Select Provider</FormLabel>
            <Select placeholder="Select Provider" onChange={handleProviderChange} value={selectedProviderId}>
              {providers?.map((p: any) => (
                <option key={p._id} value={p._id}>{p.operatorName}</option>
              ))}
            </Select>
          </FormControl>

          {/* ── Type form ── */}
          {selectedProviderId && (
            <Box borderWidth={1} borderRadius="md" overflow="hidden"
              borderColor={editingRateCardId ? 'blue.300' : 'gray.200'}>
              <Box bg={editingRateCardId ? 'blue.50' : 'gray.100'} px={3} py={2} borderBottomWidth={1}>
                <HStack justifyContent="space-between">
                  <Text fontWeight="bold" fontSize="sm"
                    color={editingRateCardId ? 'blue.700' : 'gray.600'}
                    textTransform="uppercase">
                    {editingRateCardId ? `Editing: ${ambulanceType}` : 'Add Ambulance Type'}
                  </Text>
                  {editingRateCardId && (
                    <Button size="xs" variant="ghost" colorScheme="gray" onClick={resetTypeForm}>Cancel</Button>
                  )}
                </HStack>
              </Box>

              <Box px={3} py={3} bg="white" borderBottomWidth={1}>
                <FormControl>
                  <FormLabel fontSize="sm">Ambulance Type</FormLabel>
                  <Select
                    placeholder="Select Ambulance Type"
                    value={ambulanceType}
                    onChange={(e) => setAmbulanceType(e.target.value)}
                    isDisabled={!!editingRateCardId}
                    borderColor={editingRateCardId ? 'blue.400' : 'gray.200'}
                    focusBorderColor={editingRateCardId ? 'blue.400' : 'blue.500'}
                  >
                    {availableTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                  </Select>
                </FormControl>
              </Box>

              <Box px={3} py={3} bg="white" borderBottomWidth={1}>
                <FormControl>
                  <FormLabel fontSize="sm">Vehicle Type</FormLabel>
                  <Input
                    value={vehicleType}
                    onChange={(e) => setVehicleType(e.target.value)}
                    placeholder="e.g., EECO/Bolero"
                    isDisabled={!ambulanceType}
                  />
                </FormControl>
              </Box>

              {/* Rate rows */}
              <VStack spacing={0} align="stretch">
                {rateCards.map((row, index) => (
                  <Box key={index} px={3} py={3}
                    bg={index % 2 === 0 ? 'white' : 'gray.50'}
                    borderBottomWidth={1} borderColor="gray.100">
                    <HStack spacing={3} alignItems="flex-end">
                      <FormControl>
                        <FormLabel fontSize="sm">Distance Range</FormLabel>
                        <Input size="sm"
                          value={row.distanceRange.replace(' km', '')}
                          onChange={(e) => handleRateCardChange(index, 'distanceRange', e.target.value)}
                          onBlur={() => handleInclude(index)}
                          placeholder="e.g., 0-5"
                          isDisabled={!ambulanceType}
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">Rate Type</FormLabel>
                        <Select size="sm" value={row.rateType}
                          onChange={(e) => handleRateCardChange(index, 'rateType', e.target.value)}
                          isDisabled={!ambulanceType}>
                          <option value="flat">Flat</option>
                          <option value="per km">Per km</option>
                        </Select>
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">Price</FormLabel>
                        <Input size="sm" type="number"
                          value={row.prices ?? ''}
                          onChange={(e) => handleRateCardChange(index, 'prices', e.target.value)}
                          placeholder="0"
                          isDisabled={!ambulanceType}
                        />
                      </FormControl>
                      {index !== 0 && (
                        <Button colorScheme="red" size="sm" width="40%"
                          onClick={() => setRateCards(rateCards.filter((_, i) => i !== index))}>
                          Remove
                        </Button>
                      )}
                    </HStack>
                  </Box>
                ))}
              </VStack>

              <Box px={3} py={2} bg="gray.50" borderTopWidth={1}>
                <HStack>
                  <Button size="sm" colorScheme="blue"
                    isDisabled={!ambulanceType || !vehicleType}
                    onClick={() => setRateCards([...rateCards, emptyRow()])}>
                    Add Range
                  </Button>
                  <Button size="sm" colorScheme={editingRateCardId ? 'blue' : 'teal'}
                    isLoading={saving} loadingText="Saving..."
                    isDisabled={!ambulanceType || !vehicleType}
                    onClick={handleSave}>
                    {editingRateCardId ? '✓ Update Type' : '✓ Save Type Rates'}
                  </Button>
                </HStack>
              </Box>
            </Box>
          )}

          <HStack>
            <Button colorScheme="gray" onClick={handleFullClear}>Clear</Button>
          </HStack>

        </VStack>
      </Box>

      {/* ── Right: Saved types for selected provider ── */}
      <Box flex={1} pt={1}>
        {selectedProviderId && (
          <>
            <Text fontWeight="bold" fontSize="md" mb={3} color="gray.700">
              Saved Rate Cards — {selectedProviderName}
            </Text>
            {providerRateCards.length === 0 ? (
              <Flex direction="column" align="center" justify="center" py={12}
                borderWidth={1} borderRadius="md" borderStyle="dashed" borderColor="gray.200" bg="gray.50">
                <Text color="gray.400" fontSize="sm">No rate cards saved for this provider yet</Text>
              </Flex>
            ) : (
              <Box borderWidth={1} borderRadius="md" overflow="hidden">
                <VStack spacing={0} align="stretch" divider={<Divider />}>
                  {providerRateCards.map((rc: any) => {
                    const isBeingEdited = editingRateCardId === rc._id;
                    return (
                      <Box key={rc._id}>
                        <HStack px={3} py={2} justifyContent="space-between"
                          bg={isBeingEdited ? 'blue.50' : 'white'}
                          _hover={{ bg: isBeingEdited ? 'blue.50' : 'gray.50' }}>
                          <HStack spacing={2} flex={1} minW={0}>
                            <Badge colorScheme={isBeingEdited ? 'blue' : 'teal'} fontSize="xs" flexShrink={0}>
                              {rc.rateCards?.length} {rc.rateCards?.length === 1 ? 'row' : 'rows'}
                            </Badge>
                            <Text fontSize="sm" color={isBeingEdited ? 'blue.700' : 'gray.700'} isTruncated>
                              {rc.ambulanceType}
                            </Text>
                            {rc.vehicleType && (
                              <Badge colorScheme="gray" fontSize="xs">{rc.vehicleType}</Badge>
                            )}
                            {isBeingEdited && <Badge colorScheme="blue" fontSize="xs">editing</Badge>}
                          </HStack>
                          <HStack spacing={1} flexShrink={0}>
                            {!isBeingEdited && (
                              <Button size="xs" variant="ghost" colorScheme="gray"
                                onClick={() => setExpandedType(expandedType === rc._id ? null : rc._id)}>
                                {expandedType === rc._id ? 'Hide' : 'View'}
                              </Button>
                            )}
                            <Button size="xs" variant="ghost" colorScheme="blue"
                              onClick={() => handleEditEntry(rc)}>
                              {isBeingEdited ? 'Editing...' : 'Edit'}
                            </Button>
                          </HStack>
                        </HStack>

                        <Collapse in={expandedType === rc._id && !isBeingEdited} animateOpacity>
                          <Box px={3} pb={3} bg="gray.50">
                            <TableContainer>
                              <Table size="xs" variant="simple">
                                <Thead>
                                  <Tr>
                                    <Th fontSize="10px">Distance Range</Th>
                                    <Th fontSize="10px">Rate Type</Th>
                                    <Th fontSize="10px" isNumeric>Price (₹)</Th>
                                  </Tr>
                                </Thead>
                                <Tbody>
                                  {rc.rateCards?.map((r: any, i: number) => (
                                    <Tr key={i}>
                                      <Td fontSize="xs">{r.distanceRange}</Td>
                                      <Td fontSize="xs">{r.rateType}</Td>
                                      <Td fontSize="xs" isNumeric>{r.prices}</Td>
                                    </Tr>
                                  ))}
                                </Tbody>
                              </Table>
                            </TableContainer>
                          </Box>
                        </Collapse>
                      </Box>
                    );
                  })}
                </VStack>
              </Box>
            )}
          </>
        )}
      </Box>

    </Box>
  );
}

export default AmbulanceRateCard;
